"use client";

import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { usePlayerStore } from "@gerama/stores";
import { cn } from "@gerama/utils";
import { browserCoreApi } from "@/lib/api";
import { activeLineIndex, parseLrc } from "./lrc";

import { lyricsKeys } from "./lyrics-keys";

export { lyricsKeys };

export function useLyrics(songId: number | null) {
  return useQuery({
    queryKey: lyricsKeys.song(songId ?? 0),
    queryFn: ({ signal }) => browserCoreApi.playback.lyrics(songId!, signal),
    enabled: songId != null,
    staleTime: 10 * 60_000,
    select: (lyrics) => (lyrics ? parseLrc(lyrics.content) : null),
  });
}

/**
 * Spotify's lyrics view: synced LRC scrolls with the song, the current
 * line lit and the sung ones dimmed; tapping a line seeks there. Plain
 * lyrics just render as text.
 */
export function LyricsView({ className, size = "md" }: { className?: string; size?: "md" | "lg" }) {
  const track = usePlayerStore((s) => s.current());
  const position = usePlayerStore((s) => s.positionSeconds);
  const seekTo = usePlayerStore((s) => s.seekTo);
  const songId = track?.kind === "song" ? track.id : null;
  const lyrics = useLyrics(songId);

  const lines = lyrics.data ?? [];
  const synced = lines.length > 0 && lines[0]!.time !== null;
  const active = synced ? activeLineIndex(lines, position + 0.2) : -1;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const userScrolledAt = React.useRef(0);

  // Keep the active line in view — unless the listener scrolled in the last few seconds.
  React.useEffect(() => {
    if (!synced || active < 0 || Date.now() - userScrolledAt.current < 4000) return;
    const container = containerRef.current;
    const el = container?.querySelector<HTMLElement>(`[data-line="${active}"]`);
    if (!container || !el) return;
    container.scrollTo({
      top: el.offsetTop - container.clientHeight / 3,
      behavior: "smooth",
    });
  }, [active, synced]);

  if (!songId) {
    return <Empty className={className}>متن برای این مورد در دسترس نیست.</Empty>;
  }
  if (lyrics.isPending) {
    return (
      <div className={cn("space-y-3 p-4", className)}>
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className="bg-muted/60 h-5 animate-pulse rounded"
            style={{ width: `${60 + ((i * 17) % 35)}%` }}
          />
        ))}
      </div>
    );
  }
  if (lines.length === 0) {
    return <Empty className={className}>متن این آهنگ هنوز اضافه نشده.</Empty>;
  }

  return (
    <div
      ref={containerRef}
      onWheel={() => (userScrolledAt.current = Date.now())}
      onTouchMove={() => (userScrolledAt.current = Date.now())}
      className={cn("relative overflow-y-auto scroll-smooth", className)}
      dir="auto"
    >
      <div className={cn("space-y-3 px-4 py-6", size === "lg" ? "text-2xl" : "text-lg")}>
        {lines.map((line, i) =>
          synced ? (
            <button
              key={i}
              type="button"
              data-line={i}
              onClick={() => line.time !== null && seekTo(line.time)}
              className={cn(
                "block w-full text-start leading-snug font-bold transition-all duration-300",
                i === active
                  ? "text-foreground scale-[1.02]"
                  : i < active
                    ? "text-foreground/45 hover:text-foreground/80"
                    : "text-foreground/25 hover:text-foreground/60",
              )}
            >
              {line.text || "♪"}
            </button>
          ) : (
            <p key={i} className="text-foreground/85 leading-relaxed font-semibold">
              {line.text}
            </p>
          ),
        )}
      </div>
    </div>
  );
}

function Empty({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "text-muted-foreground flex items-center justify-center p-6 text-sm",
        className,
      )}
    >
      {children}
    </div>
  );
}
